import { Modal, ModalBody, ModalContent, ModalOverlay, Stack } from "@chakra-ui/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import type { EvalCriterionDto, EvalSupplierAnswerDto } from "../../../autogen/bff-api";
import { RequirementDialogHeader } from "../../../common/components/RequirementDialogHeader";
import { AttachmentRow } from "../../../common/documents/AttachmentRow";
import {
  AnswerBlock,
  ReadonlyFieldLabel,
} from "../../sourcing-events/bid-readonly/ReadonlyAnswerCard";
import { ScoreScale } from "../bid-detail/ScoreScale";
import { StaleScoreNotice } from "./staleScore";
import { toReadonlyAnswer } from "./supplierAnswer";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  criterion: EvalCriterionDto;
  criterionName: string;
  supplierName: string;
  answer?: EvalSupplierAnswerDto | null;
  points: number | null;
  onChange: (value: number | null) => void;
  isReadOnly?: boolean;
  isStale?: boolean;
}

export const ScoreDialog = ({
  isOpen,
  onClose,
  criterion,
  criterionName,
  supplierName,
  answer,
  points,
  onChange,
  isReadOnly = false,
  isStale = false,
}: Props) => {
  const { t } = useTranslation();
  const scale = useRef<HTMLDivElement>(null);
  const attachments = answer?.attachments ?? [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl" isCentered initialFocusRef={scale}>
      <ModalOverlay />
      <ModalContent>
        <RequirementDialogHeader title={criterionName} subtitle={supplierName} />
        <ModalBody pb="6">
          <Stack spacing="6">
            <Stack spacing="2">
              <ReadonlyFieldLabel>{t("Supplier's answer")}</ReadonlyFieldLabel>
              <AnswerBlock
                answer={toReadonlyAnswer(answer, criterion)}
                notAnsweredLabel={String(t("Not answered by supplier"))}
                framed={false}
              />
              {attachments.length > 0 && (
                <Stack spacing="1" pt="1">
                  {attachments.map((attachment) => (
                    <AttachmentRow key={attachment.id} attachment={attachment} />
                  ))}
                </Stack>
              )}
            </Stack>
            <ScoreScale
              ref={scale}
              value={points}
              onChange={onChange}
              isReadOnly={isReadOnly}
              onDone={onClose}
            />
            {isStale && (
              <StaleScoreNotice onKeep={isReadOnly || points == null ? undefined : () => onChange(points)} />
            )}
          </Stack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
